/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { useConfigStore } from '@/stores';
import type { BuildingInStoreInfo, FloorInfo } from '@/services/dto/kiosk-config';
import type { TFunction } from 'i18next';
import { DUTY_FREE_BUILDING_ID, getDutyFreeBuildingName, isDutyFreePoi } from '@/utils/duty-free';

const collectShopPois = (buildings: BuildingInStoreInfo[] | undefined) => buildings?.flatMap((building) => building.filterPoiContents.shop ?? []) ?? [];

const hasDutyFreeShopPois = (buildings: BuildingInStoreInfo[] | undefined, language: string) =>
    collectShopPois(buildings).some((poi) => isDutyFreePoi(poi, language));

export const useHasMultipleBuildings = (): boolean => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const language = useConfigStore((state) => state.config.ui.language);

    return useMemo(() => {
        if (buildings && buildings.length > 1) return true;
        if (!buildings?.length) return false;
        return hasDutyFreeShopPois(buildings, language);
    }, [buildings, language]);
};

export const useShopBuildingOptions = (t: TFunction<'translation', undefined>) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);

    return useMemo(() => {
        const allBuildingOption = {
            label: t('shop.search.text.content.all_building'),
            value: 'all',
        };

        const listBuildings =
            buildings?.map((building) => ({
                label: building.name[language] || building.name['ko'],
                value: building.id,
            })) || [];

        if (hasDutyFreeShopPois(buildings, language)) {
            listBuildings.push({
                label: getDutyFreeBuildingName(language),
                value: DUTY_FREE_BUILDING_ID,
            });
        }

        return listBuildings.length > 1 ? [allBuildingOption, ...listBuildings] : listBuildings;
    }, [language, buildings, t]);
};

export const useShopFloorList = (selectedBuildingId: string | null, t: TFunction) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);

    return useMemo(() => {
        const allFloorOption = {
            id: 'all',
            label: t('shop.search.text.content.all_floor'),
            value: 'all',
            data: null,
        };

        if (!buildings) {
            return [allFloorOption];
        }

        const shopPois = collectShopPois(buildings);

        if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
            const dutyFreeFloorIds = new Set(shopPois.filter((poi) => isDutyFreePoi(poi, language)).map((poi) => poi.studioFloorId));
            const labelToFloorCodesMap = new Map<string, Set<string>>();

            buildings.forEach((building) => {
                building.floors?.forEach((floor) => {
                    if (!dutyFreeFloorIds.has(floor.floorCode)) return;

                    const label = floor.name[language] || floor.name['ko'];
                    if (!label) return;

                    if (!labelToFloorCodesMap.has(label)) {
                        labelToFloorCodesMap.set(label, new Set<string>());
                    }
                    labelToFloorCodesMap.get(label)!.add(floor.floorCode);
                });
            });

            const mergedFloors = Array.from(labelToFloorCodesMap.entries()).map(([label, codeSet]) => ({
                id: label,
                label,
                value: label,
                data: Array.from(codeSet),
            }));

            if (mergedFloors.length === 0) return [allFloorOption];

            mergedFloors.sort((a, b) => {
                const aMatch = a.label.match(/(B?)(\d+)/i);
                const bMatch = b.label.match(/(B?)(\d+)/i);

                if (!aMatch || !bMatch) return a.label.localeCompare(b.label);

                const aNum = aMatch[1].toUpperCase() === 'B' ? -parseInt(aMatch[2], 10) : parseInt(aMatch[2], 10);
                const bNum = bMatch[1].toUpperCase() === 'B' ? -parseInt(bMatch[2], 10) : parseInt(bMatch[2], 10);

                return aNum - bNum;
            });

            return [allFloorOption, ...mergedFloors];
        }

        if (selectedBuildingId === 'all') {
            const labelToFloorCodesMap = new Map<string, Set<string>>();

            buildings.forEach((building) => {
                if (!building.filterPoiContents.shop?.length) return;

                const floorCodesWithContent = new Set(building.filterPoiContents.shop.map((poi) => poi.studioFloorId));

                building.floors?.forEach((floor) => {
                    if (!floorCodesWithContent.has(floor.floorCode)) return;

                    const label = floor.name[language] || floor.name['ko'];
                    if (!labelToFloorCodesMap.has(label)) {
                        labelToFloorCodesMap.set(label, new Set<string>());
                    }
                    labelToFloorCodesMap.get(label)!.add(floor.floorCode);
                });
            });

            const mergedFloors = Array.from(labelToFloorCodesMap.entries()).map(([label, codeSet]) => ({
                id: label,
                label,
                value: label,
                data: Array.from(codeSet),
            }));

            if (mergedFloors.length === 0) return [allFloorOption];

            mergedFloors.sort((a, b) => {
                const aMatch = a.label.match(/(B?)(\d+)/i);
                const bMatch = b.label.match(/(B?)(\d+)/i);

                if (!aMatch || !bMatch) return a.label.localeCompare(b.label);

                const aNum = aMatch[1].toUpperCase() === 'B' ? -parseInt(aMatch[2], 10) : parseInt(aMatch[2], 10);
                const bNum = bMatch[1].toUpperCase() === 'B' ? -parseInt(bMatch[2], 10) : parseInt(bMatch[2], 10);

                return aNum - bNum;
            });

            return [allFloorOption, ...mergedFloors];
        }

        const buildingId = selectedBuildingId || buildings[0]?.id;
        if (!buildingId) return [allFloorOption];

        const building = buildings.find((b) => b.id === buildingId);
        if (!building || !building.filterPoiContents.shop?.length) return [allFloorOption];

        const nonDutyFreeFloorIds = new Set(
            building.filterPoiContents.shop.filter((poi) => !isDutyFreePoi(poi, language)).map((poi) => poi.studioFloorId)
        );

        const floors =
            building.floors
                ?.filter((floor) => nonDutyFreeFloorIds.has(floor.floorCode))
                .map((floor) => ({
                    id: floor.id,
                    label: floor.name[language] || floor.name['ko'],
                    value: floor.id,
                    data: floor,
                }))
                .sort((a, b) => {
                    const aMatch = a.label.match(/(B?)(\d+)/i);
                    const bMatch = b.label.match(/(B?)(\d+)/i);

                    if (!aMatch || !bMatch) return a.label.localeCompare(b.label);

                    const aNum = aMatch[1].toUpperCase() === 'B' ? -parseInt(aMatch[2], 10) : parseInt(aMatch[2], 10);
                    const bNum = bMatch[1].toUpperCase() === 'B' ? -parseInt(bMatch[2], 10) : parseInt(bMatch[2], 10);

                    return aNum - bNum;
                }) || [];

        return floors.length > 0 ? [allFloorOption, ...floors] : [allFloorOption];
    }, [language, buildings, selectedBuildingId, t]);
};

export const useShopCategories = (selectedBuildingId: string) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const mergedCategories = useConfigStore((state) => state.config.mergedCategories.shop);

    if (selectedBuildingId === 'all') {
        return mergedCategories;
    }

    const building = buildings?.find((b) => b.id === selectedBuildingId);
    if (!building) return [];

    return building.filterCategories.shop || [];
};
